import { Link } from "react-router-dom";
import {
  ArrowRight,
  Image,
  LayoutGrid,
  Package,
  Plus,
  Upload,
} from "lucide-react";
import { CATEGORIES } from "@/constants/categories";
import { useCategoryProductCounts, useProductCount } from "@/hooks/useProducts";
import { useCategoryImages, useHeroSlides } from "@/hooks/useSiteContent";

type StatCardProps = {
  label: string;
  value: string;
  detail?: string;
  icon: React.ReactNode;
  to?: string;
  loading?: boolean;
};

function StatCard({ label, value, detail, icon, to, loading }: StatCardProps) {
  const content = (
    <div className="bg-white border border-black/10 p-5 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-mono-acc uppercase tracking-[0.15em] text-vg-gray">{label}</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-vg-ink">
            {loading ? "…" : value}
          </p>
          {detail && <p className="mt-1 text-sm text-vg-gray">{detail}</p>}
        </div>
        <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-md bg-vg-navy/5 text-vg-navy">
          {icon}
        </div>
      </div>
      {to && (
        <div className="mt-auto pt-4 flex items-center gap-1 text-sm font-medium text-vg-navy">
          Manage
          <ArrowRight size={14} />
        </div>
      )}
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block h-full hover:ring-1 hover:ring-vg-navy/20 transition-shadow">
        {content}
      </Link>
    );
  }

  return content;
}

type QuickActionProps = {
  label: string;
  description: string;
  to: string;
  icon: React.ReactNode;
};

function QuickAction({ label, description, to, icon }: QuickActionProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 bg-white border border-black/10 p-4 hover:border-vg-navy/30 hover:bg-[#f8fafc] transition-colors"
    >
      <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-md bg-vg-navy text-white">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-vg-ink">{label}</p>
        <p className="text-sm text-vg-gray mt-0.5">{description}</p>
      </div>
      <ArrowRight size={16} className="shrink-0 text-vg-gray" />
    </Link>
  );
}

export const AdminDashboardPage = () => {
  const { data: productTotal, isLoading: productsLoading } = useProductCount();
  const { data: categoryCounts, isLoading: countsLoading } = useCategoryProductCounts();
  const { data: categoryImages, isLoading: imagesLoading } = useCategoryImages();
  const { data: heroSlides, isLoading: heroLoading } = useHeroSlides();

  const categoriesWithImages = (categoryImages ?? []).filter((item) => item.image_url).length;
  const categorizedProducts = categoryCounts
    ? Object.entries(categoryCounts.categories)
        .filter(([slug]) => slug !== "uncategorized")
        .reduce((sum, [, count]) => sum + count, 0)
    : 0;
  const heroConfigured = Boolean(heroSlides?.[0]?.url);

  return (
    <div data-testid="admin-dashboard-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-vg-ink">Dashboard</h1>
        <p className="text-sm text-vg-gray mt-1">
          Overview of your storefront content and catalog.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Products"
          value={productTotal?.toLocaleString() ?? "0"}
          detail="Total catalog items"
          icon={<Package size={20} strokeWidth={1.75} />}
          to="/admin/products"
          loading={productsLoading}
        />
        <StatCard
          label="Categorized"
          value={categorizedProducts.toLocaleString()}
          detail="Products assigned to a category"
          icon={<LayoutGrid size={20} strokeWidth={1.75} />}
          to="/admin/products"
          loading={countsLoading}
        />
        <StatCard
          label="Category images"
          value={`${categoriesWithImages} / ${CATEGORIES.length}`}
          detail="Landing page category cards"
          icon={<LayoutGrid size={20} strokeWidth={1.75} />}
          to="/admin/categories"
          loading={imagesLoading}
        />
        <StatCard
          label="Hero image"
          value={heroConfigured ? "Configured" : "Default"}
          detail={heroConfigured ? "Custom image active" : "Using bundled fallback"}
          icon={<Image size={20} strokeWidth={1.75} />}
          to="/admin/hero"
          loading={heroLoading}
        />
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <section>
          <h2 className="text-sm font-mono-acc uppercase tracking-[0.15em] text-vg-gray mb-4">
            Quick actions
          </h2>
          <div className="grid gap-3">
            <QuickAction
              label="Add product"
              description="Create a new catalog item"
              to="/admin/products/new"
              icon={<Plus size={18} strokeWidth={1.75} />}
            />
            <QuickAction
              label="Manage products"
              description="Edit, search, and delete catalog items"
              to="/admin/products"
              icon={<Package size={18} strokeWidth={1.75} />}
            />
            <QuickAction
              label="Update hero image"
              description="Change the landing page banner"
              to="/admin/hero"
              icon={<Upload size={18} strokeWidth={1.75} />}
            />
            <QuickAction
              label="Category images"
              description="Upload images for storefront categories"
              to="/admin/categories"
              icon={<LayoutGrid size={18} strokeWidth={1.75} />}
            />
          </div>
        </section>

        <section className="bg-white border border-black/10 p-5 h-fit">
          <h2 className="text-sm font-mono-acc uppercase tracking-[0.15em] text-vg-gray mb-4">
            Category breakdown
          </h2>
          {countsLoading ? (
            <p className="text-sm text-vg-gray">Loading…</p>
          ) : (
            <ul className="space-y-2">
              {CATEGORIES.filter((c) => c.slug !== "uncategorized").map((category) => {
                const count = categoryCounts?.categories[category.slug] ?? 0;
                return (
                  <li
                    key={category.slug}
                    className="flex items-center justify-between gap-3 text-sm border-b border-black/5 last:border-0 pb-2 last:pb-0"
                  >
                    <span className="text-vg-ink truncate">{category.name}</span>
                    <span className="font-mono-acc text-xs text-vg-gray shrink-0">{count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
